import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Search, User, Heart, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { CurrencySwitcher } from "@/components/CurrencySwitcher";
import { GlobalSearchOverlay } from "@/components/GlobalSearchOverlay";

/**
 * NavbarLuxe — minimal international-luxury navigation with mega-menu.
 * Keeps the same cart / auth wiring so the rest of the app is unchanged.
 */
const CATEGORY_GROUPS = [
  {
    heading: "Bedding",
    items: [
      { label: "Duvet Covers", slug: "duvet-covers" },
      { label: "Sheet Sets", slug: "sheet-sets" },
      { label: "Quilts & Bedspreads", slug: "quilts-bedspreads" },
      { label: "Comforters", slug: "comforters" },
      { label: "Bedskirts", slug: "bedskirts" },
      { label: "Pillow Covers", slug: "pillow-covers" },
    ],
  },
  {
    heading: "Home Textiles",
    items: [
      { label: "Curtains", slug: "curtains" },
      { label: "Towels", slug: "bath-towels" },
      { label: "Bath Robes", slug: "bath-robes" },
      { label: "Sofa Covers", slug: "sofa-covers" },
      { label: "Cushion Covers", slug: "cushion-covers" },
      { label: "Decorative Cushions", slug: "decorative-cushions" },
    ],
  },
  {
    heading: "Home & Decor",
    items: [
      { label: "Headboards", slug: "headboards" },
      { label: "Bench Cushions", slug: "bench-cushions" },
      { label: "Home Decor", slug: "home-decor" },
    ],
  },
  {
    heading: "Gifts",
    items: [
      { label: "Handmade Gifts", slug: "handmade-gifts" },
      { label: "Personalised Gifts", slug: "personalised-gifts" },
      { label: "Personalised Jewellery", slug: "personalised-jewellery" },
      { label: "Personalised Bags", slug: "personalised-bags" },
      { label: "Wedding Decor", slug: "wedding-decor" },
    ],
  },
  {
    heading: "Lifestyle",
    items: [
      { label: "Winter Jackets", slug: "winter-jackets" },
      { label: "Linen Dresses", slug: "linen-dresses" },
      { label: "Monogrammed T-Shirts", slug: "monogrammed-tshirts" },
      { label: "Minimalist Jewellery", slug: "minimalist-jewellery" },
      { label: "Baby Care", slug: "baby-care" },
      { label: "Pet Care", slug: "pet-care" },
    ],
  },
];

const LINKS = [
  { label: "Shop", to: "/shop", tid: "nav-shop" },
  { label: "Categories", to: "/shop", tid: "nav-collections", isMega: true },
  { label: "Hotel Linen", to: "/shop?category=hotel-bedding", tid: "nav-hotel" },
  { label: "About", to: "/about", tid: "nav-story" },
  { label: "SPNE Design", to: "/spne-design", tid: "nav-spne" },
  { label: "Wholesale Quote", to: "#", tid: "nav-trade", isTrade: true },
];

export const NavbarLuxe = ({ onOpenTrade }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const { openCart, totalItems } = useCart();
  const { me } = useCustomerAuth() || {};
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMegaOpen(false); }, [location.pathname]);

  const handle = (l, e) => {
    if (l.isTrade) {
      e.preventDefault();
      onOpenTrade?.();
      setOpen(false);
    }
  };

  return (
    <motion.header
      data-testid="navbar-luxe"
      className={`sticky top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-bone/95 backdrop-blur-xl border-b border-hairline" : "bg-bone"
      }`}
    >
      <div className="px-5 md:px-10 lg:px-14 py-4 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" aria-label="Anabiya Decor — Home" className="inline-flex items-center leading-none">
          <img
            src="/logo.png"
            alt="Anabiya Decor — Luxury Bedding & Home Textiles"
            width="220"
            height="80"
            className="h-14 md:h-16 lg:h-[68px] w-auto object-contain"
            fetchPriority="high"
          />
        </Link>

        <nav
          className="hidden lg:flex items-center gap-8 relative"
          onMouseLeave={() => setMegaOpen(false)}
        >
          {LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              onClick={(e) => handle(l, e)}
              onMouseEnter={() => setMegaOpen(!!l.isMega)}
              data-testid={l.tid}
              className={`overline transition-colors duration-500 text-[11px] ${l.isTrade ? "text-gold hover:text-forest" : "text-forest/80 hover:text-forest"}`}
            >
              {l.label}
            </Link>
          ))}

          {/* Mega menu */}
          <AnimatePresence>
            {megaOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.25 }}
                onMouseEnter={() => setMegaOpen(true)}
                data-testid="mega-menu"
                className="absolute top-full left-1/2 -translate-x-1/2 mt-4 bg-white border border-hairline shadow-[0_20px_60px_rgba(0,0,0,0.12)] w-[min(1100px,90vw)] p-8 lg:p-10 grid grid-cols-5 gap-8 z-50"
              >
                {CATEGORY_GROUPS.map((group) => (
                  <div key={group.heading} data-testid={`mega-col-${group.heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <div className="overline text-gold text-[10px] tracking-[3px] mb-3">{group.heading}</div>
                    <ul className="space-y-2">
                      {group.items.map((it) => (
                        <li key={it.slug}>
                          <Link
                            to={`/shop?category=${it.slug}`}
                            onClick={() => setMegaOpen(false)}
                            data-testid={`mega-link-${it.slug}`}
                            className="text-forest/75 hover:text-gold text-sm transition-colors font-light"
                          >
                            {it.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <CurrencySwitcher />
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            data-testid="nav-search"
            aria-label="Search"
            className="p-2 text-forest hover:text-gold transition-colors"
          >
            <Search size={18} strokeWidth={1.4} />
          </button>
          <Link
            to={me ? "/account" : "/account/login"}
            data-testid="nav-account"
            aria-label={me ? "Account" : "Sign in"}
            className="p-2 text-forest hover:text-gold transition-colors"
          >
            <User size={18} strokeWidth={1.4} />
          </Link>
          <button
            data-testid="nav-cart"
            onClick={openCart}
            aria-label="Bag"
            className="relative p-2 text-forest hover:text-gold transition-colors"
          >
            <ShoppingBag size={18} strokeWidth={1.4} />
            {totalItems > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-gold text-white text-[9px] flex items-center justify-center tabnum">
                {totalItems}
              </span>
            )}
          </button>
          <button
            onClick={() => setOpen((s) => !s)}
            data-testid="nav-menu-toggle"
            aria-label="Menu"
            className="lg:hidden p-2 text-forest"
          >
            {open ? <X size={20} strokeWidth={1.4} /> : <Menu size={20} strokeWidth={1.4} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
            className="lg:hidden overflow-hidden bg-bone border-t border-hairline"
          >
            <div className="px-6 py-6 flex flex-col gap-3" data-testid="mobile-menu">
              {LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={`${l.tid}-mobile`}
                  onClick={(e) => handle(l, e)}
                  className="font-display text-forest text-2xl leading-none"
                >
                  {l.label}
                </Link>
              ))}
              <div className="border-t border-hairline pt-4 mt-2 space-y-4">
                <div className="overline text-gold tracking-[3px] text-[10px]">Categories</div>
                {CATEGORY_GROUPS.map((group) => (
                  <details key={group.heading} className="group border-b border-hairline pb-2">
                    <summary className="cursor-pointer flex justify-between items-center py-1 text-forest">
                      <span className="text-base">{group.heading}</span>
                      <span className="text-forest/40 text-sm group-open:rotate-45 transition-transform">+</span>
                    </summary>
                    <ul className="mt-2 pl-4 space-y-1.5">
                      {group.items.map((it) => (
                        <li key={it.slug}>
                          <Link
                            to={`/shop?category=${it.slug}`}
                            onClick={() => setOpen(false)}
                            data-testid={`mobile-cat-${it.slug}`}
                            className="text-forest/70 text-sm hover:text-forest"
                          >
                            {it.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </details>
                ))}
              </div>
              <Link
                to={me ? "/account" : "/account/login"}
                onClick={() => setOpen(false)}
                data-testid="nav-account-mobile"
                className="font-display text-forest text-2xl leading-none border-t border-hairline pt-3 mt-2"
              >
                {me ? "My Account" : "Sign in"}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <GlobalSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </motion.header>
  );
};
